import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { MapProps, Markers } from 'ui-component/Map';
import { getLocationValuesAsync } from 'service/location-service';
import getDevicesAsync from 'service/device-service';
import { gridSpacing } from 'store/constant';
import { AccordionResponse } from 'types/overrides/chart';
import SubCard from 'ui-component/cards/SubCard';
import StartTime from 'views/variables/starttime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import { Location } from 'types/map';
import LeafletMap from 'ui-component/Map';

const Maps = () => {
    localStorage.setItem('currentPage', '/maps');

    const [expanded, setExpanded] = React.useState<boolean>(true);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [waitingForResponse, setWaitingForResponse] = React.useState<boolean>(false);

    const [devices, setDevices] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);

    const [selectedSN, setSelectedSN] = useState<string[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
    const [selectedStartTime, setSelectedStartTime] = useState<Date>(new Date(new Date().setHours(new Date().getHours() - 2)));
    const [selectedEndTime, setSelectedEndTime] = useState<Date>(new Date());
    const [markers, setMarkers] = useState<MapProps>({ markers: [] });

    const markerColorOptions: string[] = [
        'rgba(15,181,174,0.8)',
        'rgba(64,70,202,0.8)',
        'rgba(246,133,17,0.8)',
        'rgba(222,61,130,0.8)',
        'rgba(126,132,250,0.8)',
        'rgba(114,224,106,0.8)',
        'rgba(20,122,243,0.8)',
        'rgba(115,38,211,0.8)',
        'rgba(232,198,0,0.8)',
        'rgba(203,93,0,0.8)',
        'rgba(0,143,93,0.8)',
        'rgba(188,233,49,0.8)'
    ];

    let definedMarkerColors: Map<string, string> = new Map();
    // const [vars, setVars] = useState(string[])

    // const handleChangeState: ChangeEventFunc = (event) => {
    //     setState({ ...state, [event.target.name]: event.target.checked });
    // };

    useEffect(() => {
        const fetchDropDowns = async () => {
            try {
                const devicesResponse = await getDevicesAsync();
                setDevices(devicesResponse);
                console.log(devicesResponse);

                const customersResponse: Set<string> = new Set();
                devicesResponse.forEach((device: any) => {
                    // console.log(device.Customer, ' - ', device['Project Number'], ' - ', device['Vehicle ID']);
                    if (device.Customer !== null) {
                        customersResponse.add(device.Customer);
                    }
                });
                setCustomers(Array.from(customersResponse).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };
        fetchDropDowns();
    }, []);

    const handleSubmit = async (e: any) => {
        setWaitingForResponse(true);
        setExpanded(false);
        setSubmitted(true);
        e.preventDefault();

        const startTime: string = selectedStartTime !== undefined ? selectedStartTime?.toISOString().replace('T', ' ').slice(0, 23) : '';
        const endTime: string = selectedEndTime !== undefined ? selectedEndTime?.toISOString().replace('T', ' ').slice(0, 23) : '';

        const requestBody: AccordionResponse = {
            SNs: selectedSN,
            startTimeStamp: startTime,
            endTimeStamp: endTime,
            variables: [],
            chart: false,
            vehicles: selectedVehicles
        };

        setMarkers({ markers: [] });

        try {
            const response: Location[] = await getLocationValuesAsync(requestBody);

            let markersFromResponse: Markers[] = [];

            if (response == null || response == undefined || response.length == 0) {
                console.log('Response is emoty');
                setWaitingForResponse(false);
                setMarkers({ markers: [] });
                return;
            }

            let index = 0;
            response.forEach((res: any) => {
                //console.log(res[0], res[0].Lat, res[0].Long, res[0].SN);
                let backgroundColor: string | undefined;
                //console.log(definedMarkerColors.size, markerColorOptions[definedMarkerColors.size]);
                if (!definedMarkerColors.has(res[0].SN)) {
                    definedMarkerColors.set(res[0].SN, markerColorOptions[definedMarkerColors.size]);
                }
                backgroundColor = definedMarkerColors.get(res[0].SN);

                const location: Markers = {
                    coordinates: [res[0].Lat, res[0].Long],
                    message: `<strong>Device ID:</strong> ${res[0].SN}<br /><strong>Time stamp:</strong> ${res[0].timestamp}`,
                    color: backgroundColor,
                    id: index.toString()
                };

                if (res[0].Lat != null && res[0].Lat != undefined && res[0].Long != null && res[0] != undefined) {
                    markersFromResponse.push(location);
                }
                index += 1;
            });

            setMarkers({ markers: markersFromResponse });

            setWaitingForResponse(false);
        } catch (error) {
            console.error('Error submitting data:', error);
            setWaitingForResponse(false);
        }
    };

    const handleCustomerChange = (_event: any, value: string[]) => {
        setSelectedCustomers(value);
        const projectsResponse: Set<string> = new Set();
        devices.forEach((device: any) => {
            if (value.includes(device.Customer) && device['Project Number'] !== null) {
                projectsResponse.add(device['Project Number']);
            }
        });
        setProjects(Array.from(projectsResponse).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    };
    const handleProjectChange = (_event: any, value: string[]) => {
        setSelectedProjects(value);
        const vehiclesResponse: Set<string> = new Set();
        devices.forEach((device: any) => {
            if (selectedCustomers.includes(device.Customer) && value.includes(device['Project Number']) && device['Vehicle ID'] !== null) {
                vehiclesResponse.add(device['Vehicle ID']);
            }
        });
        setVehicles(Array.from(vehiclesResponse).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    };
    const handleVehicleChange = (_event: any, value: string[]) => {
        setSelectedVehicles(value);
        let serialNumbersResponse: Set<string> = new Set();
        devices.forEach((device: any) => {
            if (
                selectedCustomers.includes(device.Customer) &&
                selectedProjects.includes(device['Project Number']) &&
                value.includes(device['Vehicle ID'])
            ) {
                serialNumbersResponse.add(device['Device ID']);
            }
        });
        setSelectedSN(Array.from(serialNumbersResponse).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    };

    const handleStartTimeChange = (value: any) => {
        setSelectedStartTime(value);
    };

    const handleEndTimeChange = (value: Date) => {
        setSelectedEndTime(value);
    };

    const handleAccordianChange = (event: any) => {
        setExpanded(!expanded);
    };

    return (
        <div>
            <Accordion key={'accordion'} expanded={expanded} onChange={handleAccordianChange}>
                <AccordionSummary
                    key={'accordionSummary'}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    {!expanded ? (
                        selectedCustomers.length > 0 ? (
                            selectedCustomers.map((customer, index) => {
                                return (
                                    <Typography key={'accordionSummaryClosed'}>
                                        Customer: {customer}&emsp;&emsp;|&emsp;&emsp;Project Number: {selectedProjects[index]}
                                        &emsp;&emsp;|&emsp;&emsp;Vehicle ID: {selectedVehicles[index]}
                                    </Typography>
                                );
                            })
                        ) : (
                            <Typography key={'accordionSummaryOpen'}>Choose properties to view in a chart or table</Typography>
                        )
                    ) : (
                        <Typography key={'accordionInstructions'}>Choose properties to view in a chart or table</Typography>
                    )}
                </AccordionSummary>
                <AccordionDetails key={'accordionDetails'}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} md={6} lg={3}>
                                <SubCard title="Customer">
                                    <Grid container direction="column" spacing={3}>
                                        <Grid item>
                                            <Autocomplete
                                                key={'selectedCustomers'}
                                                multiple
                                                limitTags={2}
                                                options={customers}
                                                getOptionLabel={(option: any) => option}
                                                value={selectedCustomers}
                                                onChange={handleCustomerChange}
                                                // defaultValue={}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <SubCard title="Project Number">
                                    <Grid container direction="column" spacing={3}>
                                        <Grid item>
                                            <Autocomplete
                                                key={'selectedProjects'}
                                                multiple
                                                limitTags={2}
                                                options={projects}
                                                getOptionLabel={(option) => option}
                                                value={selectedProjects}
                                                onChange={handleProjectChange}
                                                // defaultValue={}
                                                //disabled={selectedCustomers.length === 0}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <SubCard title="Vehicle ID">
                                    <Grid container direction="column" spacing={3}>
                                        <Grid item>
                                            <Autocomplete
                                                key={'selectedVehicles'}
                                                multiple
                                                limitTags={2}
                                                options={vehicles}
                                                getOptionLabel={(option) => option}
                                                value={selectedVehicles}
                                                onChange={handleVehicleChange}
                                                // defaultValue={}
                                                //disabled={selectedCustomers.length === 0 || selectedProjects.length === 0}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} display={'flex'} flexDirection={'column'}>
                                <StartTime
                                    key={'startTime'}
                                    timeLabel="Start Time"
                                    value={selectedStartTime}
                                    onChange={handleStartTimeChange}
                                    time={selectedStartTime}
                                ></StartTime>
                                <br></br>
                                <br></br>
                                <StartTime
                                    key={'endTime'}
                                    timeLabel="End Time"
                                    value={selectedEndTime}
                                    onChange={handleEndTimeChange}
                                    time={selectedEndTime}
                                ></StartTime>
                            </Grid>
                            <Grid item lg={2} display={'flex'} alignItems={'center'} paddingTop={'24px'}>
                                <Button
                                    key={'submitButton'}
                                    variant="contained"
                                    type="submit"
                                    style={{ marginTop: '16px', marginBottom: '8px', height: '51.25px' }}
                                    disabled={
                                        selectedCustomers.length === 0 || selectedProjects.length === 0 || selectedVehicles.length === 0
                                    }
                                >
                                    Create
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </AccordionDetails>
            </Accordion>
            {submitted ? (
                waitingForResponse ? (
                    <Grid item lg={12} display={'flex'} alignItems={'center'} justifyContent={'center'} paddingTop={'5rem'}>
                        <Box key={'loadingBox'} sx={{ display: 'flex' }}>
                            <CircularProgress />
                        </Box>
                    </Grid>
                ) : (
                    <Grid style={{ marginTop: '1rem', height: '74vh' }}>
                        <LeafletMap key={'MAP'} markers={markers.markers}></LeafletMap>
                    </Grid>
                )
            ) : (
                <div></div>
            )}
        </div>
    );
};

export default Maps;
